import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, Button } from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Save,
  Store,
  Warehouse,
  Trash2,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  listenToUserCommercePartners,
  upsertCommercePartner,
  setCurrentPartner,
} from "../../redux/commercePartnersSlice";
import { RootState } from "../../redux/store";
import DefaultLogo from "../../assets/meta/logo.png";

/* ================= THEME ================= */
const BRAND_GRADIENT =
  "linear-gradient(135deg,#FF8A00,#FF3D00,#5BCBFF,#2FE6D6)";
const BLACK = "#000";
const WHITE = "#fff";
const CARD_BG = "rgba(255,255,255,0.96)";
const SHADOW = "0 14px 40px rgba(0,0,0,0.18)";
const BTN_SHADOW = "0 12px 30px rgba(255,61,0,0.55)";
const BTN_BLACK_SHADOW = "0 10px 26px rgba(0,0,0,0.45)";

declare global {
  interface Window {
    google: any;
  }
}

type FulfilledBy = {
  isFBA: boolean;
  isFBS: boolean;
};

type DeliveryZoneType = "local" | "regional" | "international";

type DeliveryZone = {
  type: DeliveryZoneType; 
  country: string;
  city: string;
  neighborhood: string;
  region: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
  deliveryFee: number;
  deliveryTime: number;
  fulfilledBy: FulfilledBy;
};

const EMPTY_ZONE = (fulfilledBy: FulfilledBy): DeliveryZone => ({
   type: "local",
  country: "",
  city: "",
  neighborhood: "",
  region: "",
  zipCode: "",
  latitude: null,
  longitude: null,
  deliveryFee: 0,
  deliveryTime: 1,
  fulfilledBy,
});

const CommerceDeliveryZones = () => {
  const dispatch = useDispatch<any>();
  const userId = useSelector((s: RootState) => s.auth.user?.userId);
  const { partners } = useSelector((s: RootState) => s.commercePartners);

  const [brandId, setBrandId] = useState("");
  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [edited, setEdited] = useState(false);

  const [brandFulfilledBy, setBrandFulfilledBy] =
    useState<FulfilledBy>({ isFBA: false, isFBS: true });

  const inputRefs = useRef<any[]>([]);
  const autoRefs = useRef<any[]>([]);


  /* LOAD PARTNERS */
  useEffect(() => {
    if (userId) dispatch(listenToUserCommercePartners(userId));
  }, [userId]);

  /* BRAND SELECT → AUTO ZONE + FULFILLMENT */
  useEffect(() => {
    if (!brandId) return;

    const partner =
      partners.find((p) => p.brandId === brandId) || null;

    dispatch(setCurrentPartner(partner));

    const fulfillment =
      partner?.fulfilledBy ?? { isFBA: false, isFBS: true };

    setBrandFulfilledBy(fulfillment);

    setZones(() => {
      if (partner?.delivery?.length) {
        return partner.delivery.map((z: DeliveryZone) => ({
          ...z,
          fulfilledBy: fulfillment,
        }));
      }
      return [EMPTY_ZONE(fulfillment)];
    });

    setExpanded(0);
    setEdited(false);
  }, [brandId]);

  /* APPLY FULFILLMENT */
  const applyFulfillment = (f: FulfilledBy) => {
    setBrandFulfilledBy(f);
    setZones((prev) => prev.map((z) => ({ ...z, fulfilledBy: f })));
    setExpanded(0);
    setEdited(true);
  };

  const updateZone = (
    i: number,
    key: keyof DeliveryZone,
    value: any
  ) => {
    setZones((prev) => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [key]: value };
      return updated;
    });
    setEdited(true);
  };

const addZone = () => {
  setZones((prev) => {
    const newZones = [...prev, EMPTY_ZONE(brandFulfilledBy)];
    setExpanded(newZones.length - 1); 
    return newZones;
  });
  setEdited(true);
};


  const saveAll = () => {
    if (!brandId) return;
    dispatch(
      upsertCommercePartner({
        brandId,
        data: {
          delivery: zones,
          fulfilledBy: brandFulfilledBy,
        },
      })
    );
    setEdited(false);
  };


  



  /* GOOGLE AUTOCOMPLETE */
useEffect(() => {
  zones.forEach((_, i) => {
    const input = inputRefs.current[i];
    if (!input || !window.google?.maps?.places) return;

    // ✅ prevent duplicate instances
    if (autoRefs.current[i]) return;

   const auto = new window.google.maps.places.Autocomplete(input, {
  types: ["address"],
  componentRestrictions: { country: "ee" }, // optional
});

    autoRefs.current[i] = auto;

    auto.addListener("place_changed", () => {
      const place = auto.getPlace();
      const c = place.address_components || [];

      const get = (t: string) =>
        c.find((x: any) => x.types.includes(t))?.long_name || "";

      updateZone(i, "country", get("country"));
      updateZone(
        i,
        "city",
        get("locality") || get("administrative_area_level_2")
      );
      updateZone(
        i,
        "neighborhood",
        get("sublocality") || get("neighborhood")
      );
      updateZone(i, "region", get("administrative_area_level_1"));
      updateZone(i, "zipCode", get("postal_code"));
      updateZone(i, "latitude", place.geometry?.location?.lat() ?? null);
      updateZone(i, "longitude", place.geometry?.location?.lng() ?? null);
    });
  });
}, [zones.length]); 



  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <h2 style={{ fontWeight: 900 }}>Delivery & Fulfillment</h2>



<Box
  sx={{
    background: "#fff8e1",
    border: "1px solid #ffe082",
    borderRadius: 3,
    p: 3,
    mb: 4,
  }}
>
  <h3 style={{ margin: 0, marginBottom: 10, fontWeight: 900 }}>
    Important Delivery Information
  </h3>

  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>
    Delivery fees are automatically selected based on the buyer’s location.
    <br /><br />

    • <strong>Local</strong> applies when the buyer is within <strong>50km</strong> of this zone.  
    <br />
    • <strong>Regional</strong> applies when the buyer is outside the local radius but within your country.  
    <br />
    • <strong>International</strong> applies when the buyer is outside your country and may vary depending on destination and package weight.
    <br /><br />

    ⚠️ If no delivery fee is configured for a matching zone, the system will automatically default to <strong>€0 (Free Delivery)</strong>.  
    The buyer will not be charged any shipping fee.
  </p>
</Box>

      {/* BRAND SELECT – 3D CARDS */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 4 }}>
        {partners.map((b) => (
          <div
            key={b.brandId}
            onClick={() => setBrandId(b.brandId)}
            style={{
              width: 170,
              borderRadius: 18,
              padding: 14,
              cursor: "pointer",
              background: CARD_BG,
              boxShadow: SHADOW,
              border:
                brandId === b.brandId
                  ? `3px solid ${BLACK}`
                  : "1px solid #e5e5e5",
              transform:
                brandId === b.brandId
                  ? "translateY(-3px)"
                  : "translateY(0)",
            }}
          >
            <img
              src={b.image || DefaultLogo}
              style={{ width: "100%", height: 80, objectFit: "contain" }}
            />
            <p style={{ textAlign: "center", fontWeight: 700 }}>
              {b.name}
            </p>
          </div>
        ))}
      </Box>

      {/* FULFILLMENT – 3D SWITCH BUTTONS */}
      {brandId && (
        <Box
          sx={{
            background: CARD_BG,
            borderRadius: 3,
            p: 3,
            mb: 4,
            boxShadow: SHADOW,
          }}
        >
          <h3 style={{ fontWeight: 900, marginBottom: 16 }}>
            How will orders be fulfilled?
          </h3>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              startIcon={<Store />}
              onClick={() => applyFulfillment({ isFBA: false, isFBS: true })}
              sx={{
                flex: 1,
                background: brandFulfilledBy.isFBS
                  ? BRAND_GRADIENT
                  : "transparent",
                color: brandFulfilledBy.isFBS ? WHITE : BLACK,
                border: brandFulfilledBy.isFBS
                  ? "none"
                  : "2px solid #000",
                boxShadow: brandFulfilledBy.isFBS ? BTN_SHADOW : "none",
                fontWeight: 900,
                py: 1.8,
              }}
            >
              Seller fulfills orders (FBS)
            </Button>

         {/*
            <Button
              startIcon={<Warehouse />}
              onClick={() => applyFulfillment({ isFBA: true, isFBS: false })}
              sx={{
                flex: 1,
                background: brandFulfilledBy.isFBA
                  ? BRAND_GRADIENT
                  : "transparent",
                color: brandFulfilledBy.isFBA ? WHITE : BLACK,
                border: brandFulfilledBy.isFBA
                  ? "none"
                  : "2px solid #000",
                boxShadow: brandFulfilledBy.isFBA ? BTN_SHADOW : "none",
                fontWeight: 900,
                py: 1.8,
              }}
            >
              Platform fulfills orders (FBA)
            </Button>
         
         
         */}
          </Box>
        </Box>
      )}

      {/* ZONES – 3D ACCORDION CARDS */}
      {zones.map((z, i) => {
        const open = expanded === i;
        return (
          <Box
            key={i}
            sx={{
              background: CARD_BG,
              borderRadius: 3,
              mb: 3,
              boxShadow: SHADOW,
            }}
          >
            <Box
              onClick={() => setExpanded(open ? null : i)}
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
            >
              <strong>{z.city || "Select city"}</strong>
              {open ? <ChevronUp /> : <ChevronDown />}
            </Box>

            {open && (
              <Box sx={{ p: 3 }}>

                <TextField
  select
  fullWidth
  label="Zone Type"
  value={z.type}
  onChange={(e) =>
    updateZone(i, "type", e.target.value)
  }
  SelectProps={{ native: true }}
  sx={{ mb: 3 }}
>
  <option value="local">Local</option>
  <option value="regional">Regional</option>
  <option value="international">International</option>
</TextField>

           <TextField
  fullWidth
  label="Delivery location"
  value={[
    z.neighborhood,
    z.city,
    z.region,
    z.country,
  ]
    .filter(Boolean)
    .join(", ")}
  inputRef={(el) => (inputRefs.current[i] = el)}
  sx={{ mb: 3 }}
/>


                <TextField
                  fullWidth
                  label="Delivery Fee (€)"
                  type="number"
                  disabled={z.fulfilledBy.isFBA}
                  value={z.deliveryFee}
                  onChange={(e) =>
                    updateZone(i, "deliveryFee", +e.target.value)
                  }
                />

                <TextField
                  fullWidth
                  label="Delivery Time (days)"
                  type="number"
                  sx={{ mt: 3 }}
                  value={z.deliveryTime}
                  onChange={(e) =>
                    updateZone(i, "deliveryTime", Math.max(1, +e.target.value))
                  }
                />

                <Button
                  startIcon={<Trash2 />}
                  sx={{ mt: 2, color: BLACK }}
                  onClick={() =>
                    setZones((prev) => prev.filter((_, idx) => idx !== i))
                  }
                >
                  Remove Zone
                </Button>
              </Box>
            )}
          </Box>
        );
      })}

      {/* 3D OVAL BUTTONS */}
      <Button
        startIcon={<Plus />}
        onClick={addZone}
        sx={{
          mt: 3,
          mr: 2,
          px: 5,
          py: 1.6,
          borderRadius: 999,
          background: BLACK,
          color: WHITE,
          boxShadow: BTN_BLACK_SHADOW,
          fontWeight: 900,
        }}
      >
        Add Zone
      </Button>

      <Button
        startIcon={<Save />}
        onClick={saveAll}
        disabled={!edited}
        sx={{
          mt: 3,
          px: 5,
          py: 1.6,
          borderRadius: 999,
          background: BRAND_GRADIENT,
          color: WHITE,
          boxShadow: BTN_SHADOW,
          opacity: edited ? 1 : 0.4,
          fontWeight: 900,
        }}
      >
        Save
      </Button>
    </Box>
  );
};

export default CommerceDeliveryZones; 